import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, addDoc, query, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { FaTable, FaTimes, FaPlus, FaTrash, FaSave, FaClipboardList, FaFilter, FaSearch, FaHome, FaHospital } from 'react-icons/fa';
import HastaneDataEntryContent from './HastaneDataEntryContent';
import { toast } from 'react-toastify';
import styles from './ManualDataEntryModal.module.css';

// Blank row template matching the Excel columns
const createBlankRow = () => ({
  _isNew: true,
  _visitId: null,
  projectId: '',
  patientId: '',
  visitNumber: '',
  windowOpenDate: '',
  targetDate: '',
  windowCloseDate: '',
  clinicianFullName: '',
  scheduledDate: '',
  scheduledTime: '',
  travelBeforeHours: '',
  travelBeforeMinutes: '',
  travelBeforeDistance: '',
  visitStartDate: '',
  visitStartTime: '',
  visitEndDate: '',
  visitEndTime: '',
  visitDurationHours: '',
  visitDurationMinutes: '',
  visitDistance: '',
  travelAfterHours: '',
  travelAfterMinutes: '',
  travelAfterDistance: '',
  issueOccurred: '',
  issueResultedInMiss: '',
  issueSource: '',
  smoCorrectionRequests: '',
  clinicalPMCorrections: '',
  correctionReason: '',
  dateInitialSourceReceived: '',
  dateLatestSourceRevision: 'N/A',
  dateSourceFinalized: '',
  wasAeSaeTaken: '',
  wasDcfCreated: '',
});

// Map a Firestore visit doc → table row
const visitToRow = (v) => {
  const ev = v.visitEvaluation || {};
  return {
    _isNew: false,
    _visitId: v.id,
    projectId: v.projectId || '',
    patientId: v.patientId || '',
    visitNumber: v.visitNumber || '',
    windowOpenDate: ev.windowOpenDate || '',
    targetDate: ev.targetDate || '',
    windowCloseDate: ev.windowCloseDate || '',
    clinicianFullName: v.clinicianFullName || ev.clinicianFullName || '',
    scheduledDate: v.firstVisitDate || v.date || '',
    scheduledTime: v.startTime || '',
    travelBeforeHours: ev.travelTimeBefore ? String(Math.floor(Number(ev.travelTimeBefore) / 60)) : '',
    travelBeforeMinutes: ev.travelTimeBefore ? String(Number(ev.travelTimeBefore) % 60) : '',
    travelBeforeDistance: ev.travelDistanceBefore || '',
    visitStartDate: v.visitStartDate || v.date || '',
    visitStartTime: v.visitStartTime || v.startTime || '',
    visitEndDate: v.visitEndDate || ev.visitEndDate || '',
    visitEndTime: v.visitEndTime || v.endTime || '',
    visitDurationHours: ev.visitDurationHours || '',
    visitDurationMinutes: ev.visitDurationMinutes || '',
    visitDistance: ev.visitDistance || '',
    travelAfterHours: ev.travelTimeAfter ? String(Math.floor(Number(ev.travelTimeAfter) / 60)) : '',
    travelAfterMinutes: ev.travelTimeAfter ? String(Number(ev.travelTimeAfter) % 60) : '',
    travelAfterDistance: ev.travelDistanceAfter || '',
    issueOccurred: ev.issueOccurred || '',
    issueResultedInMiss: ev.issueResultedInMiss || '',
    issueSource: ev.issueCategory || '',
    smoCorrectionRequests: ev.smoCorrectionRequests || '',
    clinicalPMCorrections: ev.qualityManagerCheck || '',
    correctionReason: ev.correctionReason || '',
    dateInitialSourceReceived: ev.dateInitialSourceReceived || '',
    dateLatestSourceRevision: ev.dateLatestSourceRevision || 'N/A',
    dateSourceFinalized: ev.dateSourceFinalized || '',
    wasAeSaeTaken: ev.wasAeSaeTaken || '',
    wasDcfCreated: ev.dcfCreated || '',
  };
};

// Map a table row → Firestore fields for save
const rowToFirestore = (row, employeeId, employeeName, projectId) => {
  const travelBefore = (Number(row.travelBeforeHours) || 0) * 60 + (Number(row.travelBeforeMinutes) || 0);
  const travelAfter = (Number(row.travelAfterHours) || 0) * 60 + (Number(row.travelAfterMinutes) || 0);

  return {
    patientId: row.patientId,
    visitNumber: row.visitNumber,
    date: row.scheduledDate,
    startTime: row.scheduledTime,
    visitStartDate: row.visitStartDate,
    visitStartTime: row.visitStartTime,
    visitEndDate: row.visitEndDate,
    visitEndTime: row.visitEndTime,
    endTime: row.visitEndTime,
    clinicianFullName: row.clinicianFullName,
    employeeId,
    employeeName,
    projectId: projectId || row.projectId || '',
    visitType: 'home',
    lastUpdated: new Date().toISOString(),
    visitEvaluation: {
      windowOpenDate: row.windowOpenDate,
      targetDate: row.targetDate,
      windowCloseDate: row.windowCloseDate,
      clinicianFullName: row.clinicianFullName,
      travelTimeBefore: travelBefore > 0 ? String(travelBefore) : '',
      travelDistanceBefore: row.travelBeforeDistance,
      visitDurationHours: row.visitDurationHours,
      visitDurationMinutes: row.visitDurationMinutes,
      visitDistance: row.visitDistance,
      travelTimeAfter: travelAfter > 0 ? String(travelAfter) : '',
      travelDistanceAfter: row.travelAfterDistance,
      issueOccurred: row.issueOccurred,
      issueResultedInMiss: row.issueResultedInMiss,
      issueCategory: row.issueSource,
      smoCorrectionRequests: row.smoCorrectionRequests,
      qualityManagerCheck: row.clinicalPMCorrections,
      correctionReason: row.correctionReason,
      dateInitialSourceReceived: row.dateInitialSourceReceived,
      dateLatestSourceRevision: row.dateLatestSourceRevision || 'N/A',
      dateSourceFinalized: row.dateSourceFinalized,
      wasAeSaeTaken: row.wasAeSaeTaken,
      dcfCreated: row.wasDcfCreated,
    },
  };
};

const EMPTY_FILTERS = {
  projectId: '',
  patientId: '',
  visitNumber: '',
  windowOpenDateFrom: '', windowOpenDateTo: '',
  targetDateFrom: '', targetDateTo: '',
  windowCloseDateFrom: '', windowCloseDateTo: '',
  clinicianFullName: '',
  scheduledDateFrom: '', scheduledDateTo: '',
  visitStartDateFrom: '', visitStartDateTo: '',
  visitEndDateFrom: '', visitEndDateTo: '',
  issueOccurred: '',
  issueResultedInMiss: '',
  issueSource: '',
  smoCorrectionRequests: '',
  clinicalPMCorrections: '',
  correctionReason: '',
  dateInitialSourceReceivedFrom: '', dateInitialSourceReceivedTo: '',
  dateLatestSourceRevisionFrom: '', dateLatestSourceRevisionTo: '',
  dateSourceFinalizedFrom: '', dateSourceFinalizedTo: '',
  wasAeSaeTaken: '',
  wasDcfCreated: '',
};

const ManualDataEntryModal = ({ show, onHide, workers, selectedSMO, onDataSaved }) => {
  const [activeTab, setActiveTab] = useState('home');
  const [selectedWorkerId, setSelectedWorkerId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [projectsMap, setProjectsMap] = useState({});

  // Load all projects once to map projectId → display title
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'Projects'));
        if (cancelled) return;
        const map = {};
        snap.docs.forEach(d => {
          const data = d.data() || {};
          map[d.id] = data.titleShort || data.title || '';
        });
        setProjectsMap(map);
      } catch (err) {
        console.error('Error loading projects:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [show]);

  const selectedWorker = workers.find(w => w.id === selectedWorkerId);
  const workerProjects = selectedWorker?.projects || [];

  // Extract unique patient IDs from rows for dropdown
  const uniquePatientIds = useMemo(() => {
    const patientIds = rows.map(row => row.patientId).filter(Boolean);
    return [...new Set(patientIds)].sort();
  }, [rows]);

  // Unique projects present in current rows (for filter dropdown)
  const availableRowProjects = useMemo(() => {
    const ids = [...new Set(rows.map(r => r.projectId).filter(Boolean))];
    return ids
      .map(id => ({ id, title: projectsMap[id] || id }))
      .sort((a, b) => a.title.localeCompare(b.title, 'tr'));
  }, [rows, projectsMap]);

  const setFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const hasActiveFilters = Object.values(filters).some(v => v !== '') || selectedPatientId !== '';

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSelectedPatientId('');
  };

  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  // Load visits when employee is selected
  const loadVisits = useCallback(async () => {
    if (!selectedWorkerId) { setRows([]); return; }
    setLoading(true);
    try {
      const visitsRef = collection(db, 'Visits');
      // For home visits, only filter by employee and visit type (no project filter)
      const q = query(visitsRef, where('employeeId', '==', selectedWorkerId), where('visitType', '==', 'home'));
      const snap = await getDocs(q);
      const visitRows = snap.docs.map(d => visitToRow({ id: d.id, ...d.data() }));
      // Sort: oldest first (newest at bottom)
      visitRows.sort((a, b) => {
        const dateA = a.scheduledDate || '';
        const dateB = b.scheduledDate || '';
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        const timeA = a.scheduledTime || '';
        const timeB = b.scheduledTime || '';
        return timeA.localeCompare(timeB);
      });
      setRows(visitRows);
    } catch (err) {
      console.error('Error loading visits:', err);
      toast.error('Vizitler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [selectedWorkerId]);

  useEffect(() => {
    if (show && selectedWorkerId) {
      loadVisits();
    } else if (!selectedWorkerId) {
      setRows([]);
    }
  }, [show, selectedWorkerId, loadVisits]);

  // Reset on close
  useEffect(() => {
    if (!show) {
      setSelectedWorkerId('');
      setSelectedProjectId('');
      setSelectedPatientId('');
      setRows([]);
      setFilters(EMPTY_FILTERS);
    }
  }, [show]);

  // Reset filters when worker changes
  useEffect(() => {
    setFilters(EMPTY_FILTERS);
    setSelectedPatientId('');
  }, [selectedWorkerId]);

  // Filtered rows (display only — rows state is source of truth for saving)
  const filteredRows = useMemo(() => {
    const dateInRange = (val, from, to) => {
      if (!from && !to) return true;
      if (!val) return false;
      if (from && val < from) return false;
      if (to && val > to) return false;
      return true;
    };

    return rows
      .map((row, originalIndex) => ({ row, originalIndex }))
      .filter(({ row }) => {
        // Patient ID filter (from dropdown)
        if (selectedPatientId && row.patientId !== selectedPatientId) return false;

        // Project filter
        if (filters.projectId && row.projectId !== filters.projectId) return false;
        
        // Text filters
        if (filters.patientId && !row.patientId.toLowerCase().includes(filters.patientId.toLowerCase())) return false;
        if (filters.visitNumber && !String(row.visitNumber).toLowerCase().includes(filters.visitNumber.toLowerCase())) return false;
        if (filters.clinicianFullName && !row.clinicianFullName.toLowerCase().includes(filters.clinicianFullName.toLowerCase())) return false;
        if (filters.issueSource && !row.issueSource.toLowerCase().includes(filters.issueSource.toLowerCase())) return false;
        if (filters.smoCorrectionRequests && !row.smoCorrectionRequests.toLowerCase().includes(filters.smoCorrectionRequests.toLowerCase())) return false;
        if (filters.clinicalPMCorrections && !row.clinicalPMCorrections.toLowerCase().includes(filters.clinicalPMCorrections.toLowerCase())) return false;
        if (filters.correctionReason && !row.correctionReason.toLowerCase().includes(filters.correctionReason.toLowerCase())) return false;
        // Select filters
        if (filters.issueOccurred && row.issueOccurred !== filters.issueOccurred) return false;
        if (filters.issueResultedInMiss && !row.issueResultedInMiss.toLowerCase().includes(filters.issueResultedInMiss.toLowerCase())) return false;
        if (filters.wasAeSaeTaken && row.wasAeSaeTaken !== filters.wasAeSaeTaken) return false;
        if (filters.wasDcfCreated && row.wasDcfCreated !== filters.wasDcfCreated) return false;
        // Date range filters
        if (!dateInRange(row.windowOpenDate, filters.windowOpenDateFrom, filters.windowOpenDateTo)) return false;
        if (!dateInRange(row.targetDate, filters.targetDateFrom, filters.targetDateTo)) return false;
        if (!dateInRange(row.windowCloseDate, filters.windowCloseDateFrom, filters.windowCloseDateTo)) return false;
        if (!dateInRange(row.scheduledDate, filters.scheduledDateFrom, filters.scheduledDateTo)) return false;
        if (!dateInRange(row.visitStartDate, filters.visitStartDateFrom, filters.visitStartDateTo)) return false;
        if (!dateInRange(row.visitEndDate, filters.visitEndDateFrom, filters.visitEndDateTo)) return false;
        if (!dateInRange(row.dateInitialSourceReceived, filters.dateInitialSourceReceivedFrom, filters.dateInitialSourceReceivedTo)) return false;
        if (!dateInRange(row.dateLatestSourceRevision, filters.dateLatestSourceRevisionFrom, filters.dateLatestSourceRevisionTo)) return false;
        if (!dateInRange(row.dateSourceFinalized, filters.dateSourceFinalizedFrom, filters.dateSourceFinalizedTo)) return false;
        return true;
      });
  }, [rows, filters, selectedPatientId]);

  const handleCellChange = (index, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addRow = () => {
    setRows(prev => [...prev, createBlankRow()]);
  };

  const removeRow = (index) => {
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (rows.length === 0) { toast.warn('Kaydedilecek veri bulunamadı'); return; }
    if (!selectedWorkerId) { toast.warn('Önce bir çalışan seçiniz'); return; }

    const worker = workers.find(w => w.id === selectedWorkerId);
    const employeeName = worker ? `${worker.name} ${worker.surname}` : '';

    setSaving(true);
    let savedCount = 0;
    let errorCount = 0;

    try {
      for (const row of rows) {
        const data = rowToFirestore(row, selectedWorkerId, employeeName, selectedProjectId);
        try {
          if (row._visitId && !row._isNew) {
            // Update existing
            const visitRef = doc(db, 'Visits', row._visitId);
            const currentDoc = await getDoc(visitRef);
            if (currentDoc.exists()) {
              const currentData = currentDoc.data();
              const merged = {
                ...currentData,
                ...data,
                visitEvaluation: {
                  ...currentData.visitEvaluation,
                  ...data.visitEvaluation,
                },
              };
              await updateDoc(visitRef, merged);
            } else {
              await updateDoc(visitRef, data);
            }
            savedCount++;
          } else {
            // Create new
            await addDoc(collection(db, 'Visits'), {
              ...data,
              createdAt: new Date().toISOString(),
              manualEntry: true,
            });
            savedCount++;
          }
        } catch (err) {
          console.error('Error saving row:', err);
          errorCount++;
        }
      }

      if (savedCount > 0) {
        toast.success(`${savedCount} kayıt başarıyla kaydedildi`);
        if (onDataSaved) onDataSaved();
        await loadVisits(); // reload fresh data
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} kayıt kaydedilemedi`);
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Kaydetme sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const existingCount = rows.filter(r => !r._isNew).length;
  const newCount = rows.filter(r => r._isNew).length;

  return (
    <div className={styles.overlay} onClick={onHide}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <FaTable className={styles.headerIcon} />
            <div>
              <div className={styles.headerTitle}>Manuel Veri Girişi</div>
              <div className={styles.headerSubtitle}>
                {selectedSMO ? `${selectedSMO.name} ${selectedSMO.surname}` : 'SMO'} — Vizit Değerlendirme Tablosu
              </div>
            </div>
          </div>
          <button className={styles.closeButton} onClick={onHide}>
            <FaTimes />
          </button>
        </div>

        {/* Tab Bar */}
        <div className={styles.tabBar}>
          <button
            className={`${styles.tab} ${activeTab === 'home' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('home')}
          >
            <FaHome /> Ev Visitleri
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'hastane' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('hastane')}
          >
            <FaHospital /> Hastane Visitleri
          </button>
        </div>

        {/* Selector Bar */}
        <div className={styles.selectorBar}>
          <div className={styles.selectorGroup}>
            <span className={styles.selectorLabel}>Çalışan:</span>
            <select
              className={styles.selectorSelect}
              value={selectedWorkerId}
              onChange={e => setSelectedWorkerId(e.target.value)}
            >
              <option value="">-- Çalışan Seçiniz --</option>
              {workers.map(w => (
                <option key={w.id} value={w.id}>{w.name} {w.surname}</option>
              ))}
            </select>
          </div>

          {activeTab === 'hastane' && selectedWorker && workerProjects.length > 0 && (
            <div className={styles.selectorGroup}>
              <span className={styles.selectorLabel}>Proje:</span>
              <select
                className={styles.selectorSelect}
                value={selectedProjectId}
                onChange={e => setSelectedProjectId(e.target.value)}
              >
                <option value="">-- Tüm Projeler --</option>
                {workerProjects.map((p, i) => (
                  <option key={`${p.projectId}-${i}`} value={p.projectId}>{p.projectTitle}</option>
                ))}
              </select>
            </div>
          )}

          {activeTab === 'home' && selectedWorkerId && availableRowProjects.length > 0 && (
            <div className={styles.selectorGroup}>
              <span className={styles.selectorLabel}>Proje:</span>
              <select
                className={styles.selectorSelect}
                value={filters.projectId}
                onChange={e => setFilter('projectId', e.target.value)}
              >
                <option value="">-- Tüm Projeler --</option>
                {availableRowProjects.map(p => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>
          )}

          {activeTab === 'home' && selectedWorkerId && uniquePatientIds.length > 0 && (
            <div className={styles.selectorGroup}>
              <span className={styles.selectorLabel}>Hasta ID:</span>
              <select
                className={styles.selectorSelect}
                value={selectedPatientId}
                onChange={e => setSelectedPatientId(e.target.value)}
              >
                <option value="">-- Tüm Hastalar --</option>
                {uniquePatientIds.map(patientId => (
                  <option key={patientId} value={patientId}>{patientId}</option>
                ))}
              </select>
            </div>
          )}

          {activeTab === 'home' && rows.length > 0 && (
            <span className={styles.visitCountBadge}>
              <FaClipboardList />
              {hasActiveFilters
                ? `${filteredRows.length} / ${rows.length} kayıt`
                : `${rows.length} kayıt`}
              {existingCount > 0 && <span> ({existingCount} mevcut)</span>}
              {newCount > 0 && <span> ({newCount} yeni)</span>}
            </span>
          )}

          {activeTab === 'home' && hasActiveFilters && (
            <button className={styles.clearFiltersBtn} onClick={clearFilters} title="Tüm filtreleri temizle">
              <FaFilter /> Filtreleri Temizle
            </button>
          )}

          {activeTab === 'home' && selectedWorkerId && (
            <button className={styles.addRowButton} onClick={addRow}>
              <FaPlus /> Yeni Satır Ekle
            </button>
          )}
        </div>

        {/* Hastane Tab Content */}
        {activeTab === 'hastane' && (
          <HastaneDataEntryContent
            workers={workers}
            selectedSMO={selectedSMO}
            selectedWorkerId={selectedWorkerId}
            selectedProjectId={selectedProjectId}
            onDataSaved={onDataSaved}
          />
        )}

        {/* Home Tab Table */}
        {activeTab === 'home' && <div className={styles.tableWrapper}>
          {loading ? (
            <div className={styles.emptyState}>
              <div className={styles.spinner} style={{ width: 32, height: 32, borderWidth: 3 }} />
              <span className={styles.emptyStateText}>Vizitler yükleniyor...</span>
            </div>
          ) : !selectedWorkerId ? (
            <div className={styles.emptyState}>
              <FaClipboardList className={styles.emptyStateIcon} />
              <span className={styles.emptyStateText}>Veri girişi yapmak için bir çalışan seçiniz</span>
            </div>
          ) : rows.length === 0 ? (
            <div className={styles.emptyState}>
              <FaClipboardList className={styles.emptyStateIcon} />
              <span className={styles.emptyStateText}>
                Bu çalışana ait vizit bulunamadı. Yeni satır ekleyerek veri girebilirsiniz.
              </span>
              <button className={styles.addRowButton} onClick={addRow} style={{ marginTop: '0.5rem' }}>
                <FaPlus /> Yeni Satır Ekle
              </button>
            </div>
          ) : (
            <table className={styles.dataTable}>
              <thead>
                {/* Group header row (Excel color bands) */}
                <tr className={styles.groupHeaderRow}>
                  <th className={styles.grpActions} rowSpan={showFilters ? 3 : 2} style={{ minWidth: 32 }}>#</th>
                  <th className={styles.grpIdentification} colSpan={3}>Kimlik</th>
                  <th className={styles.grpSchedule} colSpan={3}>Pencere / Zamanlama</th>
                  <th className={styles.grpSchedule} colSpan={3}>Planlanan</th>
                  <th className={styles.grpTravelBefore} colSpan={3}>Yolculuk (Öncesi)</th>
                  <th className={styles.grpVisit} colSpan={6}>Vizit Detayları</th>
                  <th className={styles.grpTravelAfter} colSpan={3}>Yolculuk (Sonrası)</th>
                  <th className={styles.grpIssue} colSpan={3}>Sorun Bilgisi</th>
                  <th className={styles.grpCorrection} colSpan={3}>Kalite İncelemesi – Düzeltme Talepleri</th>
                  <th className={styles.grpSource} colSpan={3}>Kaynak Döküman Tarihleri</th>
                  <th className={styles.grpOutcome} colSpan={2}>Sonuç</th>
                  <th className={styles.grpActions} rowSpan={showFilters ? 3 : 2} style={{ minWidth: 36 }}>Sil</th>
                </tr>
                {/* Column headers */}
                <tr className={styles.columnHeaderRow}>
                  {/* Identification */}
                  <th><span className={styles.colHeaderContent}>Proje <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>Hasta ID <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>Vizit ID <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  {/* Window / Schedule */}
                  <th><span className={styles.colHeaderContent}>Pencere Açılış <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>Hedef Tarih <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>Pencere Kapanış <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  {/* Scheduled */}
                  <th><span className={styles.colHeaderContent}>Klinisyen <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>Planlanan Tarih <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th>Planlanan Saat</th>
                  {/* Travel Before */}
                  <th>Saat</th>
                  <th>Dakika</th>
                  <th>KM</th>
                  {/* Visit */}
                  <th><span className={styles.colHeaderContent}>Başlangıç Tarihi <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th>Başlangıç Saati</th>
                  <th><span className={styles.colHeaderContent}>Bitiş Tarihi <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th>Bitiş Saati</th>
                  <th>Süre (Saat)</th>
                  <th>Süre (Dk)</th>
                  {/* Travel After */}
                  <th>Saat</th>
                  <th>Dakika</th>
                  <th>KM</th>
                  {/* Issues */}
                  <th><span className={styles.colHeaderContent}>Sorun Oluştu mu? <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>Sorun Sonucu? <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>Sorun Kaynağı <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  {/* Corrections */}
                  <th><span className={styles.colHeaderContent}>Ülke Koordinatörleri Düzeltme Talepleri <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>Klinik Proje Yöneticisi Düzeltme Talepleri <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>Düzeltme Nedeni / Yorum <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  {/* Source Dates */}
                  <th><span className={styles.colHeaderContent}>İlk Kaynak Tarihi <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>Son Revizyon Tarihi <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>Kaynak Kesinleşme <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  {/* Outcome */}
                  <th><span className={styles.colHeaderContent}>AE/SAE Gerçekleşti mi? <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                  <th><span className={styles.colHeaderContent}>DCF Oluşturuldu mu? <FaSearch className={`${styles.colSearchIcon} ${showFilters ? styles.colSearchIconActive : ''}`} onClick={toggleFilters} /></span></th>
                </tr>
                {/* Filter row */}
                {showFilters && <tr className={styles.filterHeaderRow}>
                  {/* projectId */}
                  <th>
                    <select
                      className={styles.filterSelect}
                      value={filters.projectId}
                      onChange={e => setFilter('projectId', e.target.value)}
                    >
                      <option value="">Tümü</option>
                      {availableRowProjects.map(p => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                  </th>
                  {/* patientId */}
                  <th>
                    <input
                      className={styles.filterInput}
                      placeholder="Ara..."
                      value={filters.patientId}
                      onChange={e => setFilter('patientId', e.target.value)}
                    />
                  </th>
                  {/* visitNumber */}
                  <th>
                    <input
                      className={styles.filterInput}
                      placeholder="Ara..."
                      value={filters.visitNumber}
                      onChange={e => setFilter('visitNumber', e.target.value)}
                    />
                  </th>
                  {/* windowOpenDate */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.windowOpenDateFrom}
                        onChange={e => setFilter('windowOpenDateFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.windowOpenDateTo}
                        onChange={e => setFilter('windowOpenDateTo', e.target.value)} />
                    </div>
                  </th>
                  {/* targetDate */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.targetDateFrom}
                        onChange={e => setFilter('targetDateFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.targetDateTo}
                        onChange={e => setFilter('targetDateTo', e.target.value)} />
                    </div>
                  </th>
                  {/* windowCloseDate */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.windowCloseDateFrom}
                        onChange={e => setFilter('windowCloseDateFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.windowCloseDateTo}
                        onChange={e => setFilter('windowCloseDateTo', e.target.value)} />
                    </div>
                  </th>
                  {/* clinicianFullName */}
                  <th>
                    <input
                      className={styles.filterInput}
                      placeholder="Ara..."
                      value={filters.clinicianFullName}
                      onChange={e => setFilter('clinicianFullName', e.target.value)}
                    />
                  </th>
                  {/* scheduledDate */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.scheduledDateFrom}
                        onChange={e => setFilter('scheduledDateFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.scheduledDateTo}
                        onChange={e => setFilter('scheduledDateTo', e.target.value)} />
                    </div>
                  </th>
                  {/* scheduledTime — no filter */}
                  <th />
                  {/* travelBeforeHours, travelBeforeMinutes, travelBeforeDistance — no filter */}
                  <th /><th /><th />
                  {/* visitStartDate */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.visitStartDateFrom}
                        onChange={e => setFilter('visitStartDateFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.visitStartDateTo}
                        onChange={e => setFilter('visitStartDateTo', e.target.value)} />
                    </div>
                  </th>
                  {/* visitStartTime — no filter */}
                  <th />
                  {/* visitEndDate */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.visitEndDateFrom}
                        onChange={e => setFilter('visitEndDateFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.visitEndDateTo}
                        onChange={e => setFilter('visitEndDateTo', e.target.value)} />
                    </div>
                  </th>
                  {/* visitEndTime, visitDurationHours, visitDurationMinutes — no filter */}
                  <th /><th /><th />
                  {/* travelAfterHours, travelAfterMinutes, travelAfterDistance — no filter */}
                  <th /><th /><th />
                  {/* issueOccurred */}
                  <th>
                    <select className={styles.filterSelect} value={filters.issueOccurred}
                      onChange={e => setFilter('issueOccurred', e.target.value)}>
                      <option value="">Tümü</option>
                      <option value="yes">Evet</option>
                      <option value="no">Hayır</option>
                    </select>
                  </th>
                  {/* issueResultedInMiss */}
                  <th>
                    <input
                      className={styles.filterInput}
                      placeholder="Ara..."
                      value={filters.issueResultedInMiss}
                      onChange={e => setFilter('issueResultedInMiss', e.target.value)}
                    />
                  </th>
                  {/* issueSource */}
                  <th>
                    <input className={styles.filterInput} placeholder="Ara..."
                      value={filters.issueSource} onChange={e => setFilter('issueSource', e.target.value)} />
                  </th>
                  {/* smoCorrectionRequests */}
                  <th>
                    <input className={styles.filterInput} placeholder="Ara..."
                      value={filters.smoCorrectionRequests} onChange={e => setFilter('smoCorrectionRequests', e.target.value)} />
                  </th>
                  {/* clinicalPMCorrections */}
                  <th>
                    <input className={styles.filterInput} placeholder="Ara..."
                      value={filters.clinicalPMCorrections} onChange={e => setFilter('clinicalPMCorrections', e.target.value)} />
                  </th>
                  {/* correctionReason */}
                  <th>
                    <input className={styles.filterInput} placeholder="Ara..."
                      value={filters.correctionReason} onChange={e => setFilter('correctionReason', e.target.value)} />
                  </th>
                  {/* dateInitialSourceReceived */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.dateInitialSourceReceivedFrom}
                        onChange={e => setFilter('dateInitialSourceReceivedFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.dateInitialSourceReceivedTo}
                        onChange={e => setFilter('dateInitialSourceReceivedTo', e.target.value)} />
                    </div>
                  </th>
                  {/* dateLatestSourceRevision */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.dateLatestSourceRevisionFrom}
                        onChange={e => setFilter('dateLatestSourceRevisionFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.dateLatestSourceRevisionTo}
                        onChange={e => setFilter('dateLatestSourceRevisionTo', e.target.value)} />
                    </div>
                  </th>
                  {/* dateSourceFinalized */}
                  <th>
                    <div className={styles.filterDateRange}>
                      <input type="date" className={styles.filterInputDate} value={filters.dateSourceFinalizedFrom}
                        onChange={e => setFilter('dateSourceFinalizedFrom', e.target.value)} />
                      <span className={styles.filterDateSep}>—</span>
                      <input type="date" className={styles.filterInputDate} value={filters.dateSourceFinalizedTo}
                        onChange={e => setFilter('dateSourceFinalizedTo', e.target.value)} />
                    </div>
                  </th>
                  {/* wasAeSaeTaken */}
                  <th>
                    <select className={styles.filterSelect} value={filters.wasAeSaeTaken}
                      onChange={e => setFilter('wasAeSaeTaken', e.target.value)}>
                      <option value="">Tümü</option>
                      <option value="yes">Evet</option>
                      <option value="no">Hayır</option>
                    </select>
                  </th>
                  {/* wasDcfCreated */}
                  <th>
                    <select className={styles.filterSelect} value={filters.wasDcfCreated}
                      onChange={e => setFilter('wasDcfCreated', e.target.value)}>
                      <option value="">Tümü</option>
                      <option value="yes">Evet</option>
                      <option value="no">Hayır</option>
                      <option value="n/a">N/A</option>
                    </select>
                  </th>
                </tr>}
              </thead>
              <tbody>
                {filteredRows.map(({ row, originalIndex }) => (
                  <tr key={originalIndex}>
                    {/* Row # */}
                    <td className={styles.rowNumber}>
                      {originalIndex + 1}
                      {row._isNew ? (
                        <div className={styles.newBadge}>Yeni</div>
                      ) : (
                        <div className={styles.existingBadge}>Mevcut</div>
                      )}
                    </td>

                    {/* Proje */}
                    <td className={styles.projectCell}>
                      <select 
                        className={styles.cellSelect}
                        value={row.projectId}
                        onChange={e => handleCellChange(originalIndex, 'projectId', e.target.value)}
                      >
                        <option value="">-- Proje Seçiniz --</option>
                        {Object.entries(projectsMap)
                          .sort((a, b) => {
                            const titleA = String(a[1] || '');
                            const titleB = String(b[1] || '');
                            return titleA.localeCompare(titleB, 'tr');
                          })
                          .map(([id, title]) => (
                            <option key={id} value={id}>{title || id}</option>
                          ))}
                      </select>
                    </td>

                    {/* Identification */}
                    <td>
                      <input className={styles.cellInput} value={row.patientId}
                        onChange={e => handleCellChange(originalIndex, 'patientId', e.target.value)} placeholder="—" />
                    </td>
                    <td>
                      <input className={styles.cellInput} value={row.visitNumber}
                        onChange={e => handleCellChange(originalIndex, 'visitNumber', e.target.value)} placeholder="—" />
                    </td>

                    {/* Window */}
                    <td>
                      <input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`}
                        value={row.windowOpenDate} onChange={e => handleCellChange(originalIndex, 'windowOpenDate', e.target.value)} />
                    </td>
                    <td>
                      <input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`}
                        value={row.targetDate} onChange={e => handleCellChange(originalIndex, 'targetDate', e.target.value)} />
                    </td>
                    <td>
                      <input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`}
                        value={row.windowCloseDate} onChange={e => handleCellChange(originalIndex, 'windowCloseDate', e.target.value)} />
                    </td>

                    {/* Scheduled */}
                    <td>
                      <input className={styles.cellInput} value={row.clinicianFullName}
                        onChange={e => handleCellChange(originalIndex, 'clinicianFullName', e.target.value)} placeholder="—" />
                    </td>
                    <td>
                      <input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`}
                        value={row.scheduledDate} onChange={e => handleCellChange(originalIndex, 'scheduledDate', e.target.value)} />
                    </td>
                    <td>
                      <input type="time" className={`${styles.cellInput} ${styles.cellInputTime}`}
                        value={row.scheduledTime} onChange={e => handleCellChange(originalIndex, 'scheduledTime', e.target.value)} />
                    </td>

                    {/* Travel Before */}
                    <td>
                      <input type="number" min="0" className={`${styles.cellInput} ${styles.cellInputSmall}`}
                        value={row.travelBeforeHours} onChange={e => handleCellChange(originalIndex, 'travelBeforeHours', e.target.value)} placeholder="0" />
                    </td>
                    <td>
                      <input type="number" min="0" max="59" className={`${styles.cellInput} ${styles.cellInputSmall}`}
                        value={row.travelBeforeMinutes} onChange={e => handleCellChange(originalIndex, 'travelBeforeMinutes', e.target.value)} placeholder="0" />
                    </td>
                    <td>
                      <input type="number" min="0" className={`${styles.cellInput} ${styles.cellInputNumber}`}
                        value={row.travelBeforeDistance} onChange={e => handleCellChange(originalIndex, 'travelBeforeDistance', e.target.value)} placeholder="0" />
                    </td>

                    {/* Visit Details */}
                    <td>
                      <input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`}
                        value={row.visitStartDate} onChange={e => handleCellChange(originalIndex, 'visitStartDate', e.target.value)} />
                    </td>
                    <td>
                      <input type="time" className={`${styles.cellInput} ${styles.cellInputTime}`}
                        value={row.visitStartTime} onChange={e => handleCellChange(originalIndex, 'visitStartTime', e.target.value)} />
                    </td>
                    <td>
                      <input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`}
                        value={row.visitEndDate} onChange={e => handleCellChange(originalIndex, 'visitEndDate', e.target.value)} />
                    </td>
                    <td>
                      <input type="time" className={`${styles.cellInput} ${styles.cellInputTime}`}
                        value={row.visitEndTime} onChange={e => handleCellChange(originalIndex, 'visitEndTime', e.target.value)} />
                    </td>
                    <td>
                      <input type="number" min="0" className={`${styles.cellInput} ${styles.cellInputSmall}`}
                        value={row.visitDurationHours} onChange={e => handleCellChange(originalIndex, 'visitDurationHours', e.target.value)} placeholder="0" />
                    </td>
                    <td>
                      <input type="number" min="0" max="59" className={`${styles.cellInput} ${styles.cellInputSmall}`}
                        value={row.visitDurationMinutes} onChange={e => handleCellChange(originalIndex, 'visitDurationMinutes', e.target.value)} placeholder="0" />
                    </td>

                    {/* Travel After */}
                    <td>
                      <input type="number" min="0" className={`${styles.cellInput} ${styles.cellInputSmall}`}
                        value={row.travelAfterHours} onChange={e => handleCellChange(originalIndex, 'travelAfterHours', e.target.value)} placeholder="0" />
                    </td>
                    <td>
                      <input type="number" min="0" max="59" className={`${styles.cellInput} ${styles.cellInputSmall}`}
                        value={row.travelAfterMinutes} onChange={e => handleCellChange(originalIndex, 'travelAfterMinutes', e.target.value)} placeholder="0" />
                    </td>
                    <td>
                      <input type="number" min="0" className={`${styles.cellInput} ${styles.cellInputNumber}`}
                        value={row.travelAfterDistance} onChange={e => handleCellChange(originalIndex, 'travelAfterDistance', e.target.value)} placeholder="0" />
                    </td>

                    {/* Issue Info */}
                    <td>
                      <select className={styles.cellSelect} value={row.issueOccurred}
                        onChange={e => handleCellChange(originalIndex, 'issueOccurred', e.target.value)}>
                        <option value="">—</option>
                        <option value="yes">Evet</option>
                        <option value="no">Hayır</option>
                      </select>
                    </td>
                    <td>
                      <input type="text" className={styles.cellInput}
                        value={row.issueResultedInMiss}
                        onChange={e => handleCellChange(originalIndex, 'issueResultedInMiss', e.target.value)}
                        placeholder="—" />
                    </td>
                    <td>
                      <input className={styles.cellInput} value={row.issueSource}
                        onChange={e => handleCellChange(originalIndex, 'issueSource', e.target.value)} placeholder="—" />
                    </td>

                    {/* Corrections */}
                    <td>
                      <textarea className={styles.cellTextarea} value={row.smoCorrectionRequests}
                        onChange={e => handleCellChange(originalIndex, 'smoCorrectionRequests', e.target.value)} placeholder="—" rows={1} />
                    </td>
                    <td>
                      <textarea className={styles.cellTextarea} value={row.clinicalPMCorrections}
                        onChange={e => handleCellChange(originalIndex, 'clinicalPMCorrections', e.target.value)} placeholder="—" rows={1} />
                    </td>
                    <td>
                      <textarea className={styles.cellTextarea} value={row.correctionReason}
                        onChange={e => handleCellChange(originalIndex, 'correctionReason', e.target.value)} placeholder="—" rows={1} />
                    </td>

                    {/* Source Dates */}
                    <td>
                      <input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`}
                        value={row.dateInitialSourceReceived} onChange={e => handleCellChange(originalIndex, 'dateInitialSourceReceived', e.target.value)} />
                    </td>
                    <td>
                      <div className={styles.naDateCell}>
                        <input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`}
                          value={row.dateLatestSourceRevision === 'N/A' ? '' : row.dateLatestSourceRevision}
                          disabled={row.dateLatestSourceRevision === 'N/A'}
                          onChange={e => handleCellChange(originalIndex, 'dateLatestSourceRevision', e.target.value || 'N/A')} />
                        <button
                          type="button"
                          className={`${styles.naToggleBtn} ${row.dateLatestSourceRevision === 'N/A' ? styles.naToggleBtnActive : ''}`}
                          onClick={() => handleCellChange(originalIndex, 'dateLatestSourceRevision', row.dateLatestSourceRevision === 'N/A' ? '' : 'N/A')}
                          title="N/A olarak işaretle">N/A</button>
                      </div>
                    </td>
                    <td>
                      <input type="date" className={`${styles.cellInput} ${styles.cellInputDate}`}
                        value={row.dateSourceFinalized} onChange={e => handleCellChange(originalIndex, 'dateSourceFinalized', e.target.value)} />
                    </td>

                    {/* Outcome */}
                    <td>
                      <select className={styles.cellSelect} value={row.wasAeSaeTaken}
                        onChange={e => handleCellChange(originalIndex, 'wasAeSaeTaken', e.target.value)}>
                        <option value="">—</option>
                        <option value="yes">Evet</option>
                        <option value="no">Hayır</option>
                      </select>
                    </td>
                    <td>
                      <select className={styles.cellSelect} value={row.wasDcfCreated}
                        onChange={e => handleCellChange(originalIndex, 'wasDcfCreated', e.target.value)}>
                        <option value="">—</option>
                        <option value="yes">Evet</option>
                        <option value="no">Hayır</option>
                        <option value="n/a">N/A</option>
                      </select>
                    </td>

                    {/* Delete */}
                    <td style={{ textAlign: 'center' }}>
                      <button className={styles.deleteRowButton} onClick={() => removeRow(originalIndex)} title="Satırı kaldır">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>}

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            {activeTab === 'home' && rows.length > 0 && (
              <>
                <span>
                  {hasActiveFilters
                    ? `${filteredRows.length} / ${rows.length} satır`
                    : `${rows.length} satır`}
                </span>
                {existingCount > 0 && <span className={styles.existingBadge}>Mevcut: {existingCount}</span>}
                {newCount > 0 && <span className={styles.newBadge}>Yeni: {newCount}</span>}
              </>
            )}
          </div>
          <div className={styles.footerActions}>
            <button className={styles.cancelButton} onClick={onHide}>İptal</button>
            {activeTab === 'home' && <button
              className={styles.saveButton}
              onClick={handleSave}
              disabled={saving || rows.length === 0}
            >
              {saving ? (
                <span className={styles.saving}>
                  <span className={styles.spinner} />
                  Kaydediliyor...
                </span>
              ) : (
                <>
                  <FaSave /> Tümünü Kaydet
                </>
              )}
            </button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualDataEntryModal;
